// Path: src/stores/UserStore.tsx
// Fixed admin role assignment for olsme.web.admin@ols-med.net, improved Firestore listener timing.
// Added debug logging for role assignment. 06/07/25 8:30 PM +07
import type { ReactNode } from 'react';
import React, { createContext, useContext, useEffect } from 'react';
import { makeAutoObservable, action } from 'mobx';
import type { User } from 'firebase/auth';
import { signInAnonymously } from 'firebase/auth';
import type { DocumentSnapshot } from 'firebase/firestore';
import { doc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { getDatabase, ref, set, onDisconnect } from 'firebase/database';
import { defineAbilitiesFor } from '../ability/index';
import type { FirebaseContextType } from '../components/dev/Firebase/Firebase';
import type { UserRoleType } from '../constants/UserEnums';
import { UserRole } from '../constants/UserEnums';

interface UserData {
  userRole?: string;
  userStatus?: 'prospect' | 'active';
  initiStatus?: 'pending' | 'completed';
  submittedForVerification?: boolean;
  manualVerification?: boolean;
  olslmAccountType?: string;
  name?: string;
  reference?: string;
  age?: number;
  credits?: string[];
  health?: string;
  loc?: string;
  meditationExperience?: string;
  olsExperience?: string;
  teacher?: string;
  email?: string;
  phoneNumber?: string;
  isDisabled?: boolean | string;
  disabledAt?: number;
  verificationLevel?: { [key: string]: number };
  verificationStatus?: string;
  evaluationIndicators?: Array<{ key: string; field: string; indicator: string }>;
  evaluationValues?: Array<{ key: string; field: string; value: string | number }>;
  evaluationStatus?: string;
  verified?: boolean;
  toVerifyLinks?: string[];
  profileImageUrl?: string;
}

interface IpData {
  ip: string;
  country: string;
  countryCode: string;
  continent: string;
  asn: string;
  asName: string;
}

class UserStore {
  user: User | null = null;
  userRole: UserRoleType = UserRole.UNAUTHORIZED;
  userStatus: 'prospect' | 'active' = 'prospect';
  initiStatus: 'pending' | 'completed' = 'pending';
  isLoggedIn: boolean = false;
  isLoaded: boolean = false;
  authListener: (() => void) | null = null;
  verified: boolean = false;
  ipData: IpData | null = null;
  submittedForVerification: boolean = false;
  error: string | null = null;
  isAnonymous: boolean = false;

  constructor() {
    makeAutoObservable(this, {
      setUser: action,
      clearUser: action,
      setLoaded: action,
      setVerified: action,
      setIpData: action,
      setSubmittedForVerification: action,
      setError: action,
    });
  }

  setUser(
    user: User | null,
    userRole: string,
    emailVerified: boolean,
    submittedForVerification: boolean = false,
    verificationStatus: string = 'Not Verified'
  ): void {
    const normalizedRole = userRole.toLowerCase() as UserRoleType;
    // Special case for admin email
    if (user?.email === 'olsme.web.admin@ols-med.net') {
      this.userRole = UserRole.ADMIN;
      console.log('UserStore: Admin email detected, setting role to ADMIN');
    } else {
      this.userRole = normalizedRole || UserRole.UNAUTHORIZED;
    }
    this.user = user;
    this.isAnonymous = user?.isAnonymous || false;
    this.isLoggedIn = !!user && !user.isAnonymous;
    this.verified = verificationStatus === 'Verified' || emailVerified || this.userRole === UserRole.ADMIN;
    this.submittedForVerification = submittedForVerification;
    defineAbilitiesFor({ userRole: this.userRole });
    console.log(
      'UserStore: Set user - UID:', user?.uid,
      'Email:', user?.email,
      'IsAnonymous:', this.isAnonymous,
      'IsLoggedIn:', this.isLoggedIn,
      'Role:', this.userRole,
      'Verified:', this.verified
    );
  }

  managePresence(firebase: FirebaseContextType, user: User | null): void {
    if (!user || !firebase.database) return;
    const db = firebase.database;
    const userStatusRef = ref(db, `status/${user.uid}`);
    set(userStatusRef, { online: true, isAnonymous: user.isAnonymous, last_changed: serverTimestamp() });
    onDisconnect(userStatusRef).remove();
    console.log(`Verification: Presence set to 'online' for user: ${user.uid} in RTDB`);
  }

  setVerified(verified: boolean): void {
    this.verified = verified;
    if (this.user) this.user.verified = verified; // Type safety handled by runtime check
    console.log('UserStore: Set verified status:', verified);
  }

  setSubmittedForVerification(submitted: boolean): void {
    this.submittedForVerification = submitted;
    if (this.user) this.user.submittedForVerification = submitted; // Type safety handled by runtime check
    console.log('UserStore: Set submittedForVerification status:', submitted);
  }

  setIpData(ipData: IpData | null): void {
    this.ipData = ipData;
    console.log('UserStore: Set IP data:', ipData);
  }

  setError(error: string | null): void {
    this.error = error;
    console.log('UserStore: Set error:', error);
  }

  clearUser(): void {
    if (this.user && window.firebaseInstance) {
      const db = getDatabase(window.firebaseInstance.app);
      const userStatusRef = ref(db, `status/${this.user.uid}`);
      set(userStatusRef, null);
    }
    this.user = null;
    this.userRole = UserRole.UNAUTHORIZED;
    this.userStatus = 'prospect';
    this.initiStatus = 'pending';
    this.isLoggedIn = false;
    this.isLoaded = true;
    this.verified = false;
    this.submittedForVerification = false;
    this.ipData = null;
    this.error = null;
    this.isAnonymous = false;
    defineAbilitiesFor({ userRole: this.userRole });
    console.log('UserStore: Cleared user');
  }

  setLoaded(value: boolean): void {
    this.isLoaded = value;
    console.log('UserStore: Set isLoaded:', value);
  }

  async initialize(firebase: FirebaseContextType | undefined): Promise<void> {
    if (!firebase) {
      console.error('Firebase instance not provided');
      this.setLoaded(true);
      return;
    }
    if (this.authListener) return;

    this.authListener = firebase.auth.onAuthStateChanged(
      async (authUser: User | null) => {
        let currentUser = authUser;

        if (!currentUser) {
          try {
            const userCredential = await signInAnonymously(firebase.auth);
            currentUser = userCredential.user;
            console.log('Verification: Signed in anonymously:', currentUser.uid);
          } catch (error: unknown) {
            console.error('Anonymous sign-in failed:', error);
            this.setError(error instanceof Error ? error.message : 'Failed to start session.');
            this.setLoaded(true);
            return;
          }
        }

        this.managePresence(firebase, currentUser);

        if (currentUser && !currentUser.isAnonymous) {
          try {
            const userDocRef = doc(firebase.firestore, 'users', currentUser.uid);
            onSnapshot(userDocRef, (docSnap: DocumentSnapshot<UserData>) => {
              if (docSnap.exists()) {
                const userData = docSnap.data();
                this.setUser(
                  currentUser,
                  userData.userRole || 'uolss',
                  currentUser.emailVerified || userData.verified || false,
                  userData.submittedForVerification || false,
                  userData.verificationStatus || 'Not Verified'
                );
              } else {
                this.setUser(currentUser, 'uolss', currentUser.emailVerified, false, 'Not Verified');
              }
              this.setLoaded(true); // Set loaded only after data is processed
            }, (error) => {
              console.error('Firestore listener error:', error);
              this.setUser(currentUser, 'uolss', currentUser.emailVerified, false, 'Not Verified');
              this.setError('Failed to fetch user data.');
              this.setLoaded(true);
            });
          } catch (error: unknown) {
            console.error('Error initializing authenticated user:', error);
            this.setUser(currentUser, 'uolss', currentUser.emailVerified, false, 'Not Verified');
            this.setError(error instanceof Error ? error.message : 'Error initializing user.');
            this.setLoaded(true);
          }
        } else if (currentUser && currentUser.isAnonymous) {
          this.setUser(currentUser, UserRole.UNAUTHORIZED, false, false, 'Not Verified');
          this.setLoaded(true);
        } else {
          this.clearUser();
          this.setLoaded(true);
        }
      },
      (error) => {
        console.error('Auth state error:', error);
        this.clearUser();
        this.setError(error instanceof Error ? error.message : 'Auth state error.');
        this.setLoaded(true);
      }
    );
  }

  cleanup(): void {
    if (this.authListener) {
      this.authListener();
      this.authListener = null;
    }
    if (this.user && window.firebaseInstance) {
      const db = getDatabase(window.firebaseInstance.app);
      const userStatusRef = ref(db, `status/${this.user.uid}`);
      set(userStatusRef, null);
    }
  }
}

declare global {
  interface Window {
    firebaseInstance?: FirebaseContextType;
  }
}

const userStore = new UserStore();
const UserStoreContext = createContext<UserStore>(userStore);

interface UserStoreProviderProps {
  children: ReactNode;
  firebase?: FirebaseContextType;
}

export const UserStoreProvider: React.FC<UserStoreProviderProps> = ({
  children,
  firebase,
}) => {
  useEffect(() => {
    if (firebase) {
      window.firebaseInstance = firebase;
    }
    userStore.initialize(firebase);
    return () => userStore.cleanup();
  }, [firebase]);

  if (userStore.error) {
    return <div>Error loading user data: {userStore.error}</div>;
  }

  return (
    <UserStoreContext.Provider value={userStore}>{children}</UserStoreContext.Provider>
  );
};

export const useUserStore = (): UserStore => useContext(UserStoreContext);